import React from "react";
import { getColor } from "../../utils";
import { phonePattern } from "../../../model/system/constants";

export type typeInputMaskedPhone = {
  id: string;
  name?: string;
  value: string;
  disabled?: true;
  variant?: string;
  pattern?: string;
  maxValue?: number;
  minValue?: number;
  required?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  label?: React.ReactNode;
  inputWrapClassName?: string;
  setValue: React.Dispatch<any>;
  errorMessage?: React.ReactNode;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

const affixClassName = `absolute top-0 left-0 bottom-0 bg-transparent w-14 appearance-none md:text-sm block pl-3 py-2 border-none text-gray-600`;

export const InputMaskedPhone: React.FC<typeInputMaskedPhone> = ({
  id,
  name,
  label,
  value,
  onBlur,
  variant,
  setValue,
  maxValue,
  minValue,
  onChange,
  autoFocus,
  onKeyDown,
  errorMessage,
  labelClassName,
  inputClassName,
  maxLength = 50,
  disabled = false,
  required = false,
  pattern = phonePattern,
}) => {
  const color = getColor(variant);
  const [slimValue, setSlimValue] = React.useState("");
  const [isValidValue, setIsValidValue] = React.useState(true);

  const labelStyles = [
    `block mb-1 text-sm font-medium text-gray-700`,
    labelClassName,
  ].join(" ");

  const invalidInputClassname = `appearance-none block w-full px-3 py-2 border ${
    errorMessage ? "border-red-500" : "border-gray-300"
  } ${
    disabled ? "bg-gray-100" : ""
  } rounded-md shadow-sm placeholder-gray-400 placeholder:px-0 focus:outline-none focus:ring-${
    errorMessage ? "red" : color
  }-500 focus:border-${
    errorMessage ? "red" : color
  }-500 sm:text-sm ${inputClassName}`;

  const validInputClassName = [
    `appearance-none block w-full pl-12 pr-3 py-2 border ${
      errorMessage ? "border-red-500" : "border-gray-300"
    } ${
      disabled ? "bg-gray-100" : ""
    } rounded-md shadow-sm placeholder-gray-400 placeholder:px-0 focus:outline-none focus:ring-${
      errorMessage ? "red" : color
    }-500 focus:border-${errorMessage ? "red" : color}-500 sm:text-sm`,
    inputClassName,
  ].join(" ");

  const defaultChar = React.useMemo(
    () => pattern!.replace(/[\d_]/g, ""),
    [pattern],
  );
  const startNumber = React.useMemo(
    () => "+" + pattern!.replace(/\D/g, ""),
    [pattern],
  );

  const endNumber = React.useMemo(
    () => pattern.substring(startNumber.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pattern],
  );

  // noinspection RegExpSimplifiable
  const regExpPattern = new RegExp(
    pattern
      ? "\\" +
        pattern.replace(/[_]/g, "\\d").replace("(", "\\(").replace(")", "\\)")
      : "",
  );

  React.useEffect(() => {
    if (value.match(regExpPattern)) {
      setSlimValue(value.substring(startNumber.length));
    } else {
      if (value.length > 0) {
        setIsValidValue(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!value.trim()) {
      setSlimValue("");
    }

    if (value.trim() === "" && !isValidValue) {
      setIsValidValue(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onChangeHandler = (
    e:
      | React.FocusEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const temp = (e.target as HTMLInputElement).value.split("");

    if (temp.length === 0) {
      if ((e.target as HTMLInputElement) === document.activeElement) {
        setValue("");
      }
    } else {
      const tmpValue = mapper((e.target as HTMLInputElement).value);
      setSlimValue(tmpValue);
      setValue(startNumber + tmpValue);
    }

    if (onChange) {
      onChange(e as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const mapper = (string: string) => {
    const tmp = string.startsWith(startNumber)
      ? string.substring(startNumber.length).replace(/\D/g, "")
      : string.replace(/\D/g, "");

    const res: string[] = [];
    let tmpIndex = 0;

    for (let i = 0; i < endNumber.length; i++) {
      if (tmpIndex < tmp.length) {
        if (RegExp(`[${defaultChar}]`).test(endNumber[i])) {
          res.push(endNumber[i]);
        } else {
          if (tmpIndex <= tmp.length - 1) {
            res.push(tmp[tmpIndex]);
            tmpIndex++;
          } else {
            break;
          }
        }
      }
    }
    return res.join("");
  };

  const onPastHandler = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const tmpValue = mapper(e.clipboardData.getData("text"));
    setSlimValue(tmpValue);
    setValue(startNumber + tmpValue);
  };

  return (
    <>
      {label ? (
        <label htmlFor={id} className={labelStyles}>
          {required && <span className="text-red-600 mr-1">*</span>}
          {label}
        </label>
      ) : null}
      <div>
        {isValidValue ? (
          <div className="relative">
            <input
              type="text"
              disabled={true}
              value={startNumber}
              id={id + startNumber}
              className={affixClassName}
            />
            <input
              id={id}
              name={name}
              type="text"
              max={maxValue}
              min={minValue}
              onBlur={onBlur}
              value={slimValue}
              required={required}
              disabled={disabled}
              maxLength={maxLength}
              onKeyDown={onKeyDown}
              autoFocus={autoFocus}
              onPaste={onPastHandler}
              pattern={`[${pattern}]`}
              className={validInputClassName}
              onChange={(e) => onChangeHandler(e)}
              placeholder={pattern?.substring(startNumber.length)}
            />
          </div>
        ) : (
          <div>
            <input
              id={id}
              name={name}
              type="text"
              value={value}
              max={maxValue}
              min={minValue}
              onBlur={onBlur}
              required={required}
              disabled={disabled}
              placeholder={pattern}
              onKeyDown={onKeyDown}
              autoFocus={autoFocus}
              maxLength={maxLength}
              onPaste={onPastHandler}
              pattern={`[${pattern}]`}
              className={invalidInputClassname}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        )}
        {errorMessage ? (
          <p className="text-xs mt-1 text-red-500">{errorMessage}</p>
        ) : null}
      </div>
    </>
  );
};
